"use client";

import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/** Variant swaps fade over at most this long (contract: <= 150ms, no empty frame). */
export const CROSS_FADE_MS = 150;

/**
 * Cross-fades between targeted variants: the old content fades out while the new fades in on top
 * (`popLayout`), so there is never an empty frame. With `prefers-reduced-motion` the swap is
 * instant. `enabled={false}` renders the children untouched (legacy configs keep their exact DOM).
 */
export function CrossFade({
  id,
  enabled = true,
  className = "relative",
  innerClassName,
  anchor,
  children,
}: {
  /** Changes when the picked variant changes. */
  id: string;
  enabled?: boolean;
  /** Classes of the wrapper (default `relative`); a flex parent can pass its own flex classes. */
  className?: string;
  /** Classes of the fading element. */
  innerClassName?: string;
  /**
   * `"end"`: old and new content share one grid cell, both pinned to its BOTTOM edge, so a bubble that
   * is anchored at the bottom never moves while it swaps (neither card shifts, only their opacity
   * changes). The default pops the old content out of the flow at the top, which jumps when the
   * height changes under a bottom-anchored parent.
   */
  anchor?: "end";
  children: ReactNode;
}) {
  const reduced = useReducedMotion();
  if (!enabled) return <>{children}</>;
  if (anchor === "end") {
    return (
      <div className={cn("grid grid-rows-[minmax(0,1fr)]", className)}>
        <AnimatePresence initial={false} mode="sync">
          <motion.div
            key={id}
            className={cn("col-start-1 row-start-1 max-h-full min-h-0 self-end", innerClassName)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            // The leaving content must not catch a tap meant for the new one.
            exit={{ opacity: 0, pointerEvents: "none" }}
            transition={{ duration: reduced ? 0 : CROSS_FADE_MS / 1000 }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }
  return (
    <div className={className}>
      <AnimatePresence initial={false} mode="popLayout">
        <motion.div
          key={id}
          className={innerClassName}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduced ? 0 : CROSS_FADE_MS / 1000 }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
